import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { Container, Row, Col, Alert, Spinner, Badge } from 'react-bootstrap';
import HeaderTwo from '../../components/HeaderTwo';
import { BreadcrumbBox } from '../../components/common/Breadcrumb';
import BlogSidebar from './components/BlogSidebar';
import FooterTwo from '../../components/FooterTwo';
import CommentForm from './components/CommentForm';
import { Styles } from './styles/blogDetails.js';
import { BLOGS_ENDPOINT } from '../../config/api';

const PLACEHOLDER_IMAGE = 'https://via.placeholder.com/900x500?text=Blog';

const useQuery = () => {
    const { search } = useLocation();
    return useMemo(() => new URLSearchParams(search), [search]);
};

const BlogDetails = () => {
    const query = useQuery();
    const slug = query.get('slug');
    const [blog, setBlog] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    const loadBlog = useCallback(async () => {
        if (!slug) {
            setError('Missing blog identifier.');
            setLoading(false);
            setBlog(null);
            return;
        }

        setLoading(true);
        setError('');

        try {
            const response = await fetch(`${BLOGS_ENDPOINT}/${encodeURIComponent(slug)}`);
            if (!response.ok) {
                const payload = await response.json().catch(() => ({}));
                throw new Error(payload.message || 'Unable to load blog.');
            }
            const payload = await response.json();
            setBlog(payload.blog);
        } catch (err) {
            setError(err.message || 'Unable to load blog.');
            setBlog(null);
        } finally {
            setLoading(false);
        }
    }, [slug]);

    useEffect(() => {
        loadBlog();
    }, [loadBlog]);

    const publishedDate = blog?.publishedAt ? new Date(blog.publishedAt).toLocaleDateString() : '';

    return (
        <Styles>
            <div className="main-wrapper blog-details-page">
                <HeaderTwo />
                <BreadcrumbBox title={blog?.title || 'Blog Details'} />

                <section className="blog-details-area">
                    <Container>
                        <Row>
                            <Col lg="9" md="8" sm="7">
                                {loading && (
                                    <div className="text-center my-5">
                                        <Spinner animation="border" role="status" />
                                    </div>
                                )}
                                {!loading && error && (
                                    <Alert variant="danger">{error}</Alert>
                                )}
                                {!loading && !error && blog && (
                                    <div className="blog-details-box">
                                        <div className="blog-details-banner">
                                            <img
                                                src={blog.coverImage || PLACEHOLDER_IMAGE}
                                                alt={blog.title}
                                                className="img-fluid"
                                            />
                                        </div>
                                        <div className="heading">
                                            <h4>{blog.title}</h4>
                                        </div>
                                        <div className="blog-auth_date d-flex">
                                            <div className="author-img d-flex">
                                                <div className="author-avatar">
                                                    <span>{(blog.authorName || 'A').charAt(0).toUpperCase()}</span>
                                                </div>
                                                <p>{blog.authorName || 'Admin'}</p>
                                            </div>
                                            {publishedDate && (
                                                <div className="post-date">
                                                    <p><i className="las la-calendar"></i> {publishedDate}</p>
                                                </div>
                                            )}
                                        </div>
                                        {Array.isArray(blog.tags) && blog.tags.length > 0 && (
                                            <div className="tag-list mb-3">
                                                {blog.tags.map((tag) => (
                                                    <Badge key={tag} variant="success" className="mr-2">
                                                        {tag}
                                                    </Badge>
                                                ))}
                                            </div>
                                        )}
                                        <div className="blog-details-desc">
                                            {blog.content ? (
                                                blog.content.split('\n').map((paragraph, index) => (
                                                    <p key={`${index}-${paragraph.slice(0, 8)}`}>{paragraph}</p>
                                                ))
                                            ) : (
                                                <p>No content provided for this blog.</p>
                                            )}
                                        </div>
                                        {Array.isArray(blog.comments) && blog.comments.length > 0 && (
                                            <div className="comment-list mt-4">
                                                <h5>Comments</h5>
                                                {blog.comments
                                                    .slice()
                                                    .sort((a, b) => {
                                                        const aTime = a?.createdAt ? new Date(a.createdAt).getTime() : 0;
                                                        const bTime = b?.createdAt ? new Date(b.createdAt).getTime() : 0;
                                                        return bTime - aTime;
                                                    })
                                                    .map((comment) => {
                                                        const timestamp = comment.createdAt
                                                            ? new Date(comment.createdAt).toLocaleString()
                                                            : '';
                                                        return (
                                                            <div className="comment-item" key={`${comment.email}-${comment.createdAt}`}>
                                                                <div className="comment-header">
                                                                    <div className="avatar">
                                                                        <span>{(comment.name || 'U').charAt(0).toUpperCase()}</span>
                                                                    </div>
                                                                    <div className="meta">
                                                                        <h6>{comment.name}</h6>
                                                                        {timestamp && <span>{timestamp}</span>}
                                                                    </div>
                                                                </div>
                                                                <div className="comment-body">
                                                                    <p>{comment.comment}</p>
                                                                </div>
                                                            </div>
                                                        );
                                                    })}
                                            </div>
                                        )}
                                        <div className="back-link mt-4">
                                            <Link to={process.env.PUBLIC_URL + '/blog-grid'}>
                                                <i className="las la-arrow-left"></i> Back to Blogs
                                            </Link>
                                        </div>
                                        <CommentForm slug={slug} onSubmitted={loadBlog} />
                                    </div>
                                )}
                            </Col>
                            <Col lg="3" md="4" sm="5">
                                <BlogSidebar />
                            </Col>
                        </Row>
                    </Container>
                </section>

                <FooterTwo />
            </div>
        </Styles>
    );
};

export default BlogDetails;
